/**
 * FormBuilderCanvas Component
 *
 * The main drop zone for form fields. Displays the form layout grid,
 * handles drag and drop for adding and reordering fields, and shows
 * drop indicators.
 */

import { forwardRef, memo, useCallback, useRef, useState, Fragment } from 'react';
import { Box, Text, Stack } from '@mantine/core';
import { Icon } from '@/components/ui/icons/Icon';
import {
  useFormBuilderStore,
  selectFields,
  selectFormSettings,
  selectIsEditMode,
  selectSelectedFieldId,
  selectIsDragging,
  selectDropTargetIndex,
} from '@/stores/formBuilderStore';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';
import { DND_ITEM_TYPE, DRAG_SOURCE } from './constants';
import FieldRenderer from './FieldRenderer';
import type { FormBuilderCanvasProps } from './types';
import classes from './FormBuilder.module.css';

/**
 * FormBuilderCanvas Component
 */
const FormBuilderCanvas = forwardRef<HTMLDivElement, FormBuilderCanvasProps>(
  ({ className }, ref) => {
    const fields = useFormBuilderStore(selectFields);
    const settings = useFormBuilderStore(selectFormSettings);
    const isEditMode = useFormBuilderStore(selectIsEditMode);
    const selectedFieldId = useFormBuilderStore(selectSelectedFieldId);
    const isDragging = useFormBuilderStore(selectIsDragging);
    const dropTargetIndex = useFormBuilderStore(selectDropTargetIndex);

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const {
      setDropTargetIndex,
      handleDrop,
      clearSelection,
    } = useFormBuilderStore();

    const canvasRef = useRef<HTMLDivElement>(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const handleCanvasClick = useCallback(() => {
      if (isEditMode) {
        clearSelection();
      }
    }, [isEditMode, clearSelection]);

    const handleDragOver = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        if (!isEditMode) return;

        setIsDragOver(true);

        // Calculate drop position based on mouse position
        const canvas = canvasRef.current;
        if (!canvas) return;

        const fieldElements = canvas.querySelectorAll('[data-field-index]');
        let targetIndex = fields.length;

        const mouseY = e.clientY;

        for (let i = 0; i < fieldElements.length; i++) {
          const rect = fieldElements[i].getBoundingClientRect();
          const middle = rect.top + rect.height / 2;

          if (mouseY < middle) {
            targetIndex = i;
            break;
          }
        }

        setDropTargetIndex(targetIndex);
      },
      [isEditMode, fields.length, setDropTargetIndex]
    );

    const handleDragLeave = useCallback(
      (e: React.DragEvent) => {
        // Only clear if leaving the canvas entirely
        const relatedTarget = e.relatedTarget as HTMLElement;
        if (!canvasRef.current?.contains(relatedTarget)) {
          setIsDragOver(false);
          setDropTargetIndex(null);
        }
      },
      [setDropTargetIndex]
    );

    const handleDropEvent = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);

        if (!isEditMode) return;

        const dataStr = e.dataTransfer.getData(DND_ITEM_TYPE);
        if (!dataStr) return;

        try {
          const data = JSON.parse(dataStr);
          const targetIndex = dropTargetIndex ?? fields.length;

          // Handle drop based on source
          if (data.source === DRAG_SOURCE.PALETTE) {
            // Adding new field from palette
            handleDrop(targetIndex);
          } else if (data.source === DRAG_SOURCE.CANVAS && data.fieldId) {
            // Reordering existing field
            handleDrop(targetIndex);
          }
        } catch {
          // Invalid data
        }

        setDropTargetIndex(null);
      },
      [isEditMode, dropTargetIndex, fields.length, handleDrop, setDropTargetIndex]
    );

    const renderDropIndicator = (index: number) => {
      if (!isDragging || dropTargetIndex !== index) return null;

      return (
        <Box
          key={`drop-${index}`}
          className={classes.dropIndicator}
          style={{ gridColumn: '1 / -1' }}
        />
      );
    };

    return (
      <Box
        ref={ref}
        className={`${classes.canvas} ${className || ''}`}
        onClick={handleCanvasClick}
      >
        <Box
          ref={canvasRef}
          className={classes.canvasInner}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDropEvent}
        >
          {fields.length === 0 ? (
            <EmptyCanvas isEditMode={isEditMode} isDragOver={isDragOver} />
          ) : (
            <Box
              className={classes.canvasGrid}
              data-columns={settings.columns}
              style={{
                gap: `var(--mantine-spacing-${settings.gap})`,
              }}
            >
              {fields.map((field, index) => (
                <Fragment key={field.id}>
                  {renderDropIndicator(index)}
                  <FieldRenderer
                    field={field}
                    index={index}
                    isSelected={selectedFieldId === field.id}
                    isEditMode={isEditMode}
                  />
                </Fragment>
              ))}
              {renderDropIndicator(fields.length)}
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);

FormBuilderCanvas.displayName = 'FormBuilderCanvas';

/**
 * Empty state component
 */
const EmptyCanvas = memo(
  ({ isEditMode, isDragOver }: { isEditMode: boolean; isDragOver: boolean }) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    return (
      <Box
        className={`${classes.emptyCanvas} ${isDragOver ? classes.active : ''}`}
      >
        <Icon
          name="layoutGrid"
          size={48}
          className={classes.emptyCanvasIcon}
        />
        <Text size="lg" fw={500} mb="xs" style={accessibilityStyles}>
          {isEditMode ? 'Start building your form' : 'No fields yet'}
        </Text>
        <Text size="sm" c="dimmed" ta="center" style={accessibilityStyles}>
          {isEditMode
            ? 'Drag fields from the sidebar or click a field type to add it'
            : 'Switch to edit mode to add fields'}
        </Text>
      </Box>
    );
  }
);

EmptyCanvas.displayName = 'EmptyCanvas';

export default memo(FormBuilderCanvas);
